// Delegated click/keyboard handlers for the nav dropdowns. Listening at
// the document level (rather than binding per-element on DOMContentLoaded
// / turbolinks:load) means the handlers survive Turbolinks DOM swaps —
// without it, every navigation left the new .google-tag elements unbound
// and the dropdowns just stopped responding.

class DropdownNav {
  constructor() {
    this.canHover =
      window.matchMedia && window.matchMedia("(hover: hover)").matches;

    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.hoverTimeout = null;

    document.addEventListener("click", this.onClick);
    document.addEventListener("keydown", this.onKey);

    if (this.canHover) {
      // Delegated hover open/close — mouseover/mouseout bubble, unlike
      // mouseenter/mouseleave.
      document.addEventListener("mouseover", this.onMouseOver);
      document.addEventListener("mouseout", this.onMouseOut);
    }
  }

  onClick(e) {
    // Explicit close X inside the dropdown.
    if (e.target.closest(".dropdown-close")) {
      e.preventDefault();
      e.stopPropagation();
      const trigger = e.target
        .closest(".regions-nav-dropdown")
        ?.closest(".google-tag");
      if (trigger) this.close(trigger);
      return;
    }

    // A click on a menu item inside the open dropdown — let it navigate.
    if (e.target.closest(".regions-nav-dropdown")) return;

    // A click on a trigger that has a dropdown.
    const trigger = e.target.closest(".google-tag");
    const dropdown = trigger && trigger.querySelector(".regions-nav-dropdown");

    if (trigger && dropdown) {
      // Touch / no-hover devices: tapping the trigger always toggles —
      // even if it's an anchor like 'About'. The dropdown panel
      // contains the actual navigation links.
      if (this.shouldUseClickToggle()) {
        e.preventDefault();
        this.toggle(trigger);
        return;
      }

      // Desktop with hover: anchor triggers navigate, non-anchor
      // triggers toggle on click. The dropdown also opens on hover via
      // CSS / mouseover handler below.
      if (e.target.closest("a")) return;
      e.preventDefault();
      this.toggle(trigger);
      return;
    }

    // Click outside any trigger / panel — close any open dropdown.
    this.closeAll();
  }

  onKey(e) {
    if (e.key === "Escape") this.closeAll();
  }

  onMouseOver(e) {
    if (this.shouldUseClickToggle()) return;

    const trigger = this.triggerFromEvent(e);
    if (!trigger) return;
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    this.open(trigger);
  }

  onMouseOut(e) {
    if (this.shouldUseClickToggle()) return;

    const trigger = this.triggerFromEvent(e);
    if (!trigger) return;
    // Don't close if the mouse moved into the dropdown panel itself.
    const next = e.relatedTarget;
    if (next instanceof Element && trigger.contains(next)) return;
    this.hoverTimeout = setTimeout(() => this.close(trigger), 150);
  }

  triggerFromEvent(e) {
    if (!(e.target instanceof Element)) return null;
    const trigger = e.target.closest(".google-tag");
    if (!trigger) return null;
    if (!trigger.querySelector(".regions-nav-dropdown")) return null;
    return trigger;
  }

  shouldUseClickToggle() {
    return (
      !this.canHover ||
      (window.matchMedia && window.matchMedia("(max-width: 767px)").matches)
    );
  }

  toggle(trigger) {
    const dropdown = trigger.querySelector(".regions-nav-dropdown");
    if (!dropdown) return;
    if (dropdown.classList.contains("open")) {
      this.close(trigger);
    } else {
      this.open(trigger);
    }
  }

  open(trigger) {
    const dropdown = trigger.querySelector(".regions-nav-dropdown");
    if (!dropdown || dropdown.classList.contains("open")) return;
    this.closeAll();
    dropdown.classList.add("open");
    trigger.setAttribute("aria-expanded", "true");
  }

  close(trigger) {
    const dropdown = trigger.querySelector(".regions-nav-dropdown");
    if (dropdown) dropdown.classList.remove("open");
    trigger.setAttribute("aria-expanded", "false");
  }

  closeAll() {
    document
      .querySelectorAll(".regions-nav-dropdown.open")
      .forEach((d) => d.classList.remove("open"));
    document
      .querySelectorAll('.google-tag[aria-expanded="true"]')
      .forEach((t) => t.setAttribute("aria-expanded", "false"));
  }
}

// Single instance, document-level listeners — no per-element binding,
// so Turbolinks navigations don't strand stale handlers on removed DOM
// nodes.
if (!window.dropdownNavInstance) {
  window.dropdownNavInstance = new DropdownNav();
}

export default DropdownNav;
